import Card from "components/Card/Card";
import { ControlledInput } from "components/Card/ControlledInput";
import { MaskedInput, TextInput } from "components/inputs";
import React, { useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import {
  useGetFileByIdQuery,
  usePatchFileByIdMutation,
} from "services/file-api.ts";
import { useToken } from "utils/context";

const ReferringPartyInformationForm = ({
  isReadOnly = false,
}: { isReadOnly?: boolean } = {}) => {
  const { fileId } = useParams<{ fileId: string }>();
  const { accessToken } = useToken();
  const [patchFileById] = usePatchFileByIdMutation();

  const {
    control,
    reset,
    formState: { errors },
  } = useForm();

  const { data: fileData } = useGetFileByIdQuery(
    { id: fileId },
    { skip: !fileId || !accessToken },
  );

  useEffect(() => {
    if (fileData) {
      reset(fileData, { keepDirtyValues: true });
    }
  }, [fileData, reset]);

  // accessToken is read but not in the dependency array below; pre-existing,
  // not introduced by KAB-3379, and consistent with this project's
  // react-hooks/exhaustive-deps being off (see .eslintrc.cjs), so leaving as
  // is here.
  const handleSave = useCallback(
    async (field: string, value: string | number) => {
      if (!fileId || !accessToken) return;

      try {
        await patchFileById({
          id: fileId,
          data: { [field]: value },
        });
      } catch (error) {
        console.error(`Failed to update ${field}:`, error);
      }
    },
    [fileId, patchFileById],
  );

  return (
    <Card heading="Referring Party Information">
      <ControlledInput
        control={control}
        name="referring_party_name"
        label="Referring Party Name"
        onSave={(value) => handleSave("referring_party_name", value)}
        errors={errors}
        componentType={TextInput}
        readOnly={isReadOnly}
      />
      <ControlledInput
        control={control}
        name="referring_party_phone"
        label="Phone"
        onSave={(value) => handleSave("referring_party_phone", value)}
        errors={errors}
        componentType={MaskedInput}
        mask="phone"
        readOnly={isReadOnly}
      />
      <ControlledInput
        control={control}
        name="referring_party_email"
        label="Email"
        onSave={(value) => handleSave("referring_party_email", value)}
        errors={errors}
        componentType={TextInput}
        readOnly={isReadOnly}
      />
      <ControlledInput
        control={control}
        name="creator.email"
        label="File created by"
        onSave={(value) => handleSave("creator.email", value)}
        errors={errors}
        componentType={TextInput}
        readOnly={true}
      />
    </Card>
  );
};

export default React.memo(ReferringPartyInformationForm);
